import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTasks,
  faPlus,
  faCheckSquare,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faFileAlt,
  faFlagCheckered,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, BookOpen } from "lucide-react";
import {
  showErrorToast,
  showConfirmDialog,
  showSuccessToast,
} from "../../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../../utils/confirmDelete";
import TaskFilter from "../components/TaskFilter";
import {
  getAllTasks,
  getTasksExportSummary,
  deleteTaskByStatus,
  reportTask,
  getCurrentApprover,
  getCurrentApproversBatch,
} from "../../../../api/hrm/tasks/task_info";
import { hasPermission, canExportHrmTasksData } from "../../../../utils/permissionUtils";

import TaskRegisterModal from "../components/TaskRegisterModal";
import TaskAssignModal from "../components/TaskAssignModal";
import TaskCardWaiting from "../components/cards/TaskCardWaiting";
import TaskCardApproved from "../components/cards/TaskCardApproved";
import TaskCardReported from "../components/cards/TaskCardReported";
import TaskPopupWaiting from "../components/popups/TaskPopupWaitingNew";
import TaskPopupApproved from "../components/popups/TaskPopupApprovedNew";
import TaskPopupReported from "../components/popups/TaskPopupReportedNew";
import TaskPopupCompleted from "../components/popups/TaskPopupCompletedNew";
import TaskMainCompletedTable from "../components/tables/TaskMainTable";
import { startTaskMainListTour } from "./taskMainListTour";
import { downloadTasksExcel, downloadTasksSummaryExcel } from "../taskExportExcel";


// Giả định bạn có các component này, nếu không hãy điều chỉnh tên cho đúng
const CARD_COMPONENTS = {
  approved: TaskCardApproved,
  reported: TaskCardReported,
  waiting: TaskCardWaiting,
};

const TaskMainListPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [isMobile, setIsMobile] = useState(false);
  const [isAllowed, setIsAllowed] = useState(true); // giới hạn thời gian hiển thị
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedTaskInfo, setSelectedTaskInfo] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const notificationTarget =
    location.state?.notificationTarget?.moduleCode === "hrm.tasks"
      ? location.state.notificationTarget
      : null;
  const handleQuickTour = useCallback(() => {
    window.setTimeout(() => startTaskMainListTour(), 80);
  }, []);
  const handleOpenManual = useCallback(() => {
    const guideUrl = `${window.location.origin}/guides/hrm-tasks-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  }, []);

  const handleExportTasksXlsx = useCallback(async (payload) => {
    if (!canExportHrmTasksData()) {
      showErrorToast("Bạn không có quyền kết xuất dữ liệu công việc.");
      return;
    }
    const { from_date, to_date, keyword, branch_id, department_id } = payload || {};
    if (!from_date || !to_date) {
      showErrorToast("Chọn đủ khoảng ngày.");
      return;
    }
    if (from_date > to_date) {
      showErrorToast("Từ ngày không được sau đến ngày.");
      return;
    }
    try {
      const data = await getAllTasks({
        from_date,
        to_date,
        ...(keyword ? { keyword } : {}),
        ...(branch_id ? { branch_id } : {}),
        ...(department_id ? { department_id } : {}),
      });
      const items = data.items || [];
      if (items.length === 0) {
        showErrorToast("Không có dữ liệu công việc trong khoảng đã chọn.");
        return;
      }
      const safeFrom = String(from_date).replace(/-/g, "");
      const safeTo = String(to_date).replace(/-/g, "");
      await downloadTasksExcel(items, {
        from_date,
        to_date,
        filename: `cong-viec_${safeFrom}_${safeTo}.xlsx`,
      });
      showSuccessToast("Đã tải file Excel.");
    } catch (err) {
      console.error("❌ Export tasks xlsx:", err);
      showErrorToast("Không thể xuất Excel.");
    }
  }, []);

  const handleExportTasksSummaryXlsx = useCallback(async (payload) => {
    if (!canExportHrmTasksData()) {
      showErrorToast("Bạn không có quyền kết xuất dữ liệu công việc.");
      return;
    }
    const { from_date, to_date, keyword, branch_id, department_id } = payload || {};
    if (!from_date || !to_date) {
      showErrorToast("Chọn đủ khoảng ngày.");
      return;
    }
    if (from_date > to_date) {
      showErrorToast("Từ ngày không được sau đến ngày.");
      return;
    }
    try {
      const data = await getTasksExportSummary({
        from_date,
        to_date,
        ...(keyword ? { keyword } : {}),
        ...(branch_id ? { branch_id } : {}),
        ...(department_id ? { department_id } : {}),
      });
      const items = data.items || [];
      if (items.length === 0) {
        showErrorToast("Không có dữ liệu tổng hợp trong khoảng đã chọn.");
        return;
      }
      const safeFrom = String(from_date).replace(/-/g, "");
      const safeTo = String(to_date).replace(/-/g, "");
      await downloadTasksSummaryExcel(items, {
        from_date,
        to_date,
        filename: `tong-hop-cong-viec_${safeFrom}_${safeTo}.xlsx`,
      });
      showSuccessToast("Đã tải file Excel tổng hợp.");
    } catch (err) {
      console.error("❌ Export tasks summary xlsx:", err);
      showErrorToast(err?.message || "Không thể xuất Excel tổng hợp.");
    }
  }, []);

  const handleSelect = (info, status) => {
    setSelectedTaskInfo(info);
    setSelectedStatus(status);
    setShowPopup(true);
  };


  // ✅ Giới hạn giờ thao tác (7h30–21h)
  useEffect(() => {
    const checkTime = () => {
      const [hour, minute] = new Date()
        .toLocaleTimeString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh", hour12: false })
        .split(":")
        .map(Number);
      const total = hour * 60 + minute;
      setIsAllowed(total >= 450 && total <= 1260); // 7h30–21h
    };
    checkTime();
    const timer = setInterval(checkTime, 60000);
    return () => clearInterval(timer);
  }, []);

  // =========================
  // 🔹 Xóa công việc chờ duyệt có xác nhận
  // =========================
  const handleDelete = async (task) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.task_waiting_day,
      context: { workDayLabel: dayjs(task.work_day).format("DD/MM/YYYY") },
    });

    if (!confirm.isConfirmed) return; // ✅ Kiểm tra tại đây, ở file cha

    try {
      const localDate = dayjs(task.work_day).startOf("day").format("YYYY-MM-DD");
      await deleteTaskByStatus({
        employee_id: task.employee_id,
        work_day: localDate,
        statuses: "waiting",
      });
      showSuccessToast("Đã xóa toàn bộ công việc chờ duyệt trong ngày!");
      fetchTasks();
    } catch (error) {
      console.error("❌ Lỗi khi xóa công việc:", error);
      showErrorToast("Không thể xóa công việc. Vui lòng thử lại!");
    }
  };


  // =========================
  // 🔹 Báo cáo
  // =========================
  const handleReport = async (task, payload) => {
    await reportTask(task.id, payload);
    fetchTasks();
  };

  // =========================
  // 🔹 Lấy dữ liệu
  // =========================
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await getAllTasks({ ...filters, summary_mode: 1 });
      const rawTasks = data.items || [];

      // Giữ nguyên UX: bảng completed vẫn dùng dữ liệu chi tiết theo từng task
      const completedTasksData = rawTasks.filter(
        (t) =>
          (t.status || "").toLowerCase() === "completed" ||
          (t.status || "").toLowerCase() === "rejected" ||
          (t.status || "").toLowerCase() === "rejected-report"
      );
      setCompletedTasks(completedTasksData);

      const grouped = {};
      rawTasks.forEach((t) => {
        const key = `${t.employee_id}_${t.work_day}_${t.status}`;
        if (!grouped[key]) {
          grouped[key] = {
            id: t.id,
            employee_id: t.employee_id,
            employee_name: t.employee_name,
            department_name: t.department_name,
            branch_name: t.branch_name,
            work_day: t.work_day,
            status: t.status,
            avatar_url: t.avatar_url || null,
            task_list: [],
            score: t.score || null,
            tags: t.tags || [],
          };
        }

        // grouped[key].task_list.push(t.taskname);
        grouped[key].task_list.push({
          taskname: t.taskname,
          task_type: t.task_type,
        });
      });

      const merged = Object.values(grouped);

      // 🧩 Gọi API batch thay vì từng task riêng
      const ids = merged.map((t) => t.id).filter(Boolean);
      let batchRes = { data: [] };
      if (ids.length > 0) {
        batchRes = await getCurrentApproversBatch(ids);
      }
      const approverMap = {};
      (batchRes.data || []).forEach(item => {
        approverMap[item.task_id] = item.approvers || [];
      });

      const tasksWithFlag = merged.map(t => {
        const approvers = approverMap[t.id] || [];
        const canApprove = approvers.some(a => a.id === currentUser?.id);
        return { ...t, can_approve: canApprove };
      });

      setTasks(tasksWithFlag);
    } catch (err) {
      console.error("❌ Lỗi tải danh sách công việc:", err);
      showErrorToast("Không thể tải danh sách công việc");
    } finally {
      setLoading(false);
      setFirstLoadDone(true);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters?.from_date && filters?.to_date) {
        fetchTasks();
      }
    }, 300); // đợi 300ms để filters sẵn sàng
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isNeedMyApprovalView = selectedDashboard === "need_my_approval";

  const needMyApprovalTasks = (tasks || []).filter((t) => {
    if (!t.can_approve) return false;
    const status = (t.status || "").toLowerCase();
    return status === "waiting" || status === "reported";
  });

  const groupByStatus = (status) => {
    const source = isNeedMyApprovalView ? needMyApprovalTasks : tasks || [];
    return source.filter(
      (t) => (t.status || "").toLowerCase() === (status || "").toLowerCase()
    );
  };

  // ✅ Đếm số trên dashboard (đếm theo toàn bộ data, không phụ thuộc tab đang chọn)
  const getDashboardCount = (key) => {
    if (key === "need_my_approval") return needMyApprovalTasks.length;
    if (key === "completed") return completedTasks?.length || 0;
    return (tasks || []).filter(
      (t) => (t.status || "").toLowerCase() === (key || "").toLowerCase()
    ).length;
  };

  const dashboardItems = [
    {
      key: "need_my_approval",
      label: "Cần bạn duyệt",
      icon: faExclamationCircle,
      color: "text-red-600",
      tailwind: "text-red-600 bg-red-50",
    },
    {
      key: "waiting",
      label: "Chờ duyệt",
      icon: faClock,
      color: "text-yellow-500",
      tailwind: "text-yellow-600 bg-yellow-50",
    },
    {
      key: "approved",
      label: "Đã duyệt",
      icon: faCheckCircle,
      color: "text-green-600",
      tailwind: "text-green-600 bg-green-50",
    },
    {
      key: "reported",
      label: "Đã báo cáo",
      icon: faFileAlt,
      color: "text-blue-500",
      tailwind: "text-blue-600 bg-blue-50",
    },
    {
      key: "completed",
      label: "Hoàn thành",
      icon: faFlagCheckered,
      color: "text-gray-600",
      tailwind: "text-gray-600 bg-gray-50",
    },
  ];

  // =========================
  // 🔹 Popup chi tiết
  // =========================
  const renderPopup = () => {
    if (!selectedTaskInfo) return null;

    const commonProps = {
      isOpen: showPopup,
      onClose: () => {
        setShowPopup(false);
        setSelectedTaskInfo(null);
        document.body.style.overflow = "auto";
      },
      employeeId: selectedTaskInfo?.employee_id,
      employeeName: selectedTaskInfo?.employee_name,
      branchName: selectedTaskInfo?.branch_name,
      workDay: selectedTaskInfo?.work_day,
      taskList: selectedTaskInfo?.task_list || [],
      onUpdated: fetchTasks,
    };

    switch (selectedStatus) {
      case "waiting":
        return <TaskPopupWaiting {...commonProps} />;
      case "approved":
        return <TaskPopupApproved {...commonProps} onReport={handleReport} />;
      case "reported":
        return <TaskPopupReported {...commonProps} />;
      case "completed":
        return (
          <TaskPopupCompleted
            isOpen={showPopup}
            onClose={() => setShowPopup(false)}
            taskId={selectedTaskInfo?.id}
            employeeId={selectedTaskInfo?.employee_id}
            workDay={selectedTaskInfo?.work_day}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!notificationTarget?.referenceId) return;

    const targetId = String(notificationTarget.referenceId);
    const groupedTask = tasks.find((item) => String(item.id) === targetId);
    const completedTask = completedTasks.find((item) => String(item.id) === targetId);
    const targetTask = groupedTask || completedTask;
    if (!targetTask) return;

    const fallbackStatus = (targetTask.status || "").toLowerCase() || "waiting";
    const targetStatus = notificationTarget.targetType || fallbackStatus;

    handleSelect(targetTask, targetStatus);
    navigate(location.pathname, { replace: true, state: null });
  }, [notificationTarget, tasks, completedTasks]);

  // =========================
  // 🔹 Render giao diện
  // =========================
  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-8" data-tour="tasks-header">
        <div className="flex items-center gap-4">
          <div className="bg-red-700 p-2.5 rounded-2xl text-white shadow-lg shadow-red-200">
            <FontAwesomeIcon
              icon={faTasks}
              className="text-xl w-6 h-6 flex items-center justify-center"
            />
          </div>

          <div>
            <h1 className="text-xl font-black text-gray-800 tracking-tight uppercase">
              Quản lý <span className="text-red-700">công việc</span>
            </h1>

            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider text-nowrap">
                <span className="text-red-600">
                  {isNeedMyApprovalView ? needMyApprovalTasks.length : tasks.length}
                </span>{" "}
                công việc đang hiển thị
                {isNeedMyApprovalView ? " · cần bạn duyệt" : ""}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenManual}
            title="Hướng dẫn sử dụng"
            aria-label="Hướng dẫn sử dụng"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          >
            <BookOpen className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden />
          </button>
          <button
            type="button"
            onClick={handleQuickTour}
            title="Product tour"
            aria-label="Product tour"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-red-200 bg-red-50/90 text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100"
          >
            <Sparkles className="h-4 w-4 shrink-0 text-red-600" strokeWidth={2.25} aria-hidden />
          </button>
        </div>
      </div>

      {/* Bộ lọc */}
      <div data-tour="tasks-filter">
        <TaskFilter
          onFilterChange={setFilters}
          onAssignClick={() => setShowAssignDialog(true)}
          onCreateClick={() => setShowRegisterDialog(true)}
          onExportXlsxRequest={handleExportTasksXlsx}
          onExportSummaryXlsxRequest={handleExportTasksSummaryXlsx}
        />
      </div>

      {/* 🟢 Dashboard tổng hợp trạng thái (Phiên bản Nâng cấp Mobile) */}
      <div className="my-6" data-tour="tasks-dashboard">
        {isMobile ? (
          // 👉 UI MOBILE: Phong cách Tab Bar hiện đại, tối giản
          <div className="bg-white border border-gray-100 rounded-full shadow-sm p-1 flex items-center justify-between gap-0.5 overflow-x-auto">
            {dashboardItems.map((item) => {
              const isActive = selectedDashboard === item.key;
              const count = getDashboardCount(item.key);
              const [textColor, bgColor] = item.tailwind.split(" ");

              return (
                <button
                  key={item.key}
                  title={item.label}
                  onClick={() => setSelectedDashboard((prev) => (prev === item.key ? null : item.key))}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 rounded-full transition-all duration-300 outline-none min-w-0
              ${isActive
                      ? `${bgColor} shadow-inner scale-105`
                      : "hover:bg-gray-50 active:scale-95"
                    }
            `}
                >
                  <FontAwesomeIcon
                    icon={item.icon}
                    className={`text-sm ${isActive ? textColor : "text-gray-400"}`}
                  />
                  <span className={`text-sm font-bold leading-none ${isActive ? textColor : "text-gray-600"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          // 👉 UI DESKTOP
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 my-6">
            {dashboardItems.map((item) => {
              const isActive = selectedDashboard === item.key;
              const count = getDashboardCount(item.key);
              const accent = item.color.split("-")[1]; // red | yellow | green | blue | gray

              return (
                <div
                  key={item.key}
                  onClick={() => setSelectedDashboard((prev) => (prev === item.key ? null : item.key))}
                  className={`cursor-pointer flex items-center gap-4 rounded-3xl p-4 transition-all duration-200
                  ${isActive
                      ? `border-2 border-${accent}-300 bg-${accent}-50 shadow-lg scale-[1.02]`
                      : "bg-white border border-gray-200 hover:shadow-md hover:scale-[1.01]"
                    }
                `}
                >
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 border ${item.color.replace("text", "border")}`}
                  >
                    <FontAwesomeIcon icon={item.icon} className={`${item.color} text-xl`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{item.label}</p>
                    <p className={`text-lg font-semibold ${item.color}`}>{count}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {renderPopup()}

      {loading ? (
        <div className="text-gray-500 text-center py-10 animate-pulse">
          <p>🔄 Đang tải dữ liệu công việc...</p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col gap-6"
          data-tour="tasks-list-sections"
        >
          {(selectedDashboard === null ||
            selectedDashboard === "waiting" ||
            isNeedMyApprovalView) && (
            <TaskGroup
              title="Công việc chờ duyệt"
              tasks={groupByStatus("waiting")}
              type="waiting"
              currentUser={currentUser}
              onSelect={handleSelect}
              onDelete={handleDelete}
              loading={loading}
              firstLoadDone={firstLoadDone}
            />
          )}

          {(selectedDashboard === null || selectedDashboard === "approved") && (
            <TaskGroup
              title="Công việc đã duyệt"
              tasks={groupByStatus("approved")}
              type="approved"
              currentUser={currentUser}
              onSelect={handleSelect}
              loading={loading}
              firstLoadDone={firstLoadDone}
            />
          )}

          {(selectedDashboard === null ||
            selectedDashboard === "reported" ||
            isNeedMyApprovalView) && (
            <TaskGroup
              title="Công việc đã báo cáo"
              tasks={groupByStatus("reported")}
              type="reported"
              currentUser={currentUser}
              onSelect={handleSelect}
              loading={loading}
              firstLoadDone={firstLoadDone}
            />
          )}

          {(selectedDashboard === null || selectedDashboard === "completed") && (
            <div data-tour="tasks-completed">
              <h3 className="font-medium mb-2">
                Công việc đã đóng, hoàn thành, chưa đạt ({completedTasks.length})
              </h3>
              <TaskMainCompletedTable
                tasks={completedTasks || []}
                onSelect={(task) => handleSelect(task, "completed")}
              />
            </div>
          )}

          {isNeedMyApprovalView &&
            !loading &&
            firstLoadDone &&
            needMyApprovalTasks.length === 0 && (
              <div className="rounded-2xl border border-dashed border-red-200 bg-red-50/40 px-6 py-10 text-center">
                <p className="text-sm font-bold text-red-700">
                  Không có công việc cần bạn duyệt
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Chọn lại thẻ khác trên dashboard để xem toàn bộ danh sách.
                </p>
              </div>
            )}
        </motion.div>
      )}

      {/* ✅ Floating buttons (mobile) */}
      {isMobile && !showPopup && (
        <div data-tour="tasks-mobile-actions">
          {/* 🔹 Chỉ giới hạn nút Đăng ký công việc */}
          {hasPermission("hrm.tasks", "C") && (
            <button
              disabled={!isAllowed}
              onClick={() => isAllowed && setShowRegisterDialog(true)}
              title={
                isAllowed
                  ? "Đăng ký công việc"
                  : "Chỉ cho phép đăng ký trong khung giờ 7:30 - 21:00"
              }
              className={`fixed bottom-20 right-5 z-40 w-14 h-14 rounded-full flex items-center justify-center text-xl transition-all ${isAllowed
                ? "bg-red-500 text-white shadow-lg hover:scale-105 active:scale-95"
                : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-70"
                }`}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          )}


          {/* 🔹 Nút Giao việc: không giới hạn giờ */}
          {hasPermission("hrm.tasks", "A") && (
            <button
              onClick={() => setShowAssignDialog(true)}
              title="Giao việc"
              className="fixed bottom-36 right-5 z-40 bg-red-500 text-white w-14 h-14 rounded-full shadow-lg hover:scale-105 flex items-center justify-center text-lg transition-all active:scale-95"
            >
              <FontAwesomeIcon icon={faCheckSquare} />
            </button>
          )}
        </div>
      )}

      {/* Modal đăng ký */}
      {showRegisterDialog && (
        <TaskRegisterModal
          isOpen={showRegisterDialog}
          onClose={() => {
            setShowRegisterDialog(false);
            fetchTasks();
          }}
          onSaved={fetchTasks}
        />
      )}

      {/* Modal giao việc */}
      {showAssignDialog && (
        <TaskAssignModal
          isOpen={showAssignDialog}
          onClose={() => setShowAssignDialog(false)}
          onSaved={fetchTasks}
        />
      )}
    </div>
  );
};

export default TaskMainListPage;

// ======================================================
// 🔹 Component nhóm card
// ======================================================
// const TaskGroup = ({
//   title,
//   tasks = [],
//   type = "waiting",
//   currentUser,
//   onSelect,
//   onDelete,
//   loading,
//   firstLoadDone,
// }) => {
//   // 🟢 Xác định là mobile hay desktop
//   const isMobile = window.innerWidth < 768; // breakpoint Tailwind md:768px

//   // 🟡 Hiển thị shimmer khi đang tải hoặc chưa tải xong lần đầu
//   if (!firstLoadDone || loading)
//     return (
//       <div className="mb-6">
//         <h3 className="font-medium mb-2">{title}</h3>

//         <div
//           className={`grid gap-4 animate-pulse ${
//             isMobile ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
//           }`}
//         >
//           {[...Array(isMobile ? 1 : 4)].map((_, i) => (
//             <div
//               key={i}
//               className="h-28 rounded-xl bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]"
//             ></div>
//           ))}
//         </div>

//         <style>
//           {`
//             @keyframes shimmer {
//               0% { background-position: -200% 0; }
//               100% { background-position: 200% 0; }
//             }
//           `}
//         </style>
//       </div>
//     );

//   // 🟢 Không có dữ liệu sau khi tải xong
//   if (!tasks.length)
//     return (
//       <div className="mb-6">
//         <h3 className="font-medium mb-2">{title} (0)</h3>
//         <p className="text-gray-500 text-sm">Không có dữ liệu</p>
//       </div>
//     );

//   // ✅ Có dữ liệu thật
//   return (
//     <div className="mb-6">
//       <h3 className="font-medium mb-2">
//         {title} ({tasks.length})
//       </h3>

//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
//         {tasks.map((t) => {
//           const key = `${t.employee_id}_${t.work_day}`;
//           switch (type) {
//             case "approved":
//               return (
//                 <TaskCardApproved
//                   key={key}
//                   task={t}
//                   currentUserId={currentUser?.id}
//                   onSelect={(task) => onSelect(task, "approved")}
//                 />
//               );
//             case "reported":
//               return (
//                 <TaskCardReported
//                   key={key}
//                   task={t}
//                   currentUserId={currentUser?.id}
//                   onSelect={(task) => onSelect(task, "reported")}
//                 />
//               );
//             default:
//               return (
//                 <TaskCardWaiting
//                   key={key}
//                   task={t}
//                   currentUserId={currentUser?.id}
//                   onSelect={(task) => onSelect(task, "waiting")}
//                   onDelete={onDelete}
//                 />
//               );
//           }
//         })}
//       </div>
//     </div>
//   );
// };

const TaskGroup = ({
  title,
  tasks = [],
  type = "waiting",
  currentUser,
  onSelect,
  onDelete,
  loading,
  firstLoadDone,
}) => {
  // 1. Nếu đang tải: Hiển thị Skeleton để tránh Layout Shift (nhảy khung hình)
  if (!firstLoadDone || loading) {
    return (
      <section className="mb-8 px-4 sm:px-0">
        <div className="h-6 w-32 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 rounded-xl bg-gray-100 animate-pulse border border-gray-50" />
          ))}
        </div>
      </section>
    );
  }

  // 2. Nếu đã tải xong mà KHÔNG có task: Trả về null để ẩn hoàn toàn Component
  if (!tasks || tasks.length === 0) {
    return null;
  }

  // 3. Mapping Card Component để code sạch hơn
  const CardComponent = {
    approved: TaskCardApproved,
    reported: TaskCardReported,
    waiting: TaskCardWaiting,
  }[type] || TaskCardWaiting;

  // 4. Render UI chính khi có dữ liệu
  return (
    <section className="mb-8 px-4 sm:px-0 animate-in fade-in duration-500">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-black text-gray-700 text-lg">{title}</h3>
        <span className="bg-red-50 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full border border-red-100">
          {tasks.length} việc
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tasks.map((t) => (
          <CardComponent
            key={`${t.employee_id}_${t.work_day}`}
            task={t}
            currentUserId={currentUser?.id}
            onSelect={(task) => onSelect(task, type)}
            onDelete={type === "waiting" ? onDelete : undefined}
          />
        ))}
      </div>
    </section>
  );
};